// Servicio para manejar la integración con Firebase Realtime Database
import { initializeApp } from "firebase/app";
import { getDatabase, ref, set, get, push, child, update, onValue } from "firebase/database";
import { RSVPEntry } from './rsvpService';

// Configuración de Firebase
const firebaseConfig = {
  apiKey: "AIzaSyC3MSZyxUrsZWDKLgBJzbMxODaC3RTYnuE",
  authDomain: "boda-emi-rsvp.firebaseapp.com",
  databaseURL: "https://boda-emi-rsvp-default-rtdb.firebaseio.com",
  projectId: "boda-emi-rsvp",
  storageBucket: "boda-emi-rsvp.firebasestorage.app",
  messagingSenderId: "1010877264663",
  appId: "1:1010877264663:web:494e75aaa0ba484572fd52"
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

/**
 * Guardar un registro RSVP en Firebase
 */
export const saveRSVPToFirebase = async (entry: RSVPEntry): Promise<boolean> => {
  try {
    // Crear una referencia a la ubicación donde guardaremos los datos
    const rsvpRef = ref(database, 'rsvps/' + entry.id);
    
    // Guardar los datos en Firebase
    await set(rsvpRef, entry);
    
    console.log('Datos guardados exitosamente en Firebase:', entry.id);
    return true;
  } catch (error) {
    console.error('Error al guardar datos en Firebase:', error);
    return false;
  }
};

// Interfaz para el nuevo esquema de datos RSVP
export interface NuevoRSVPEntry {
  nombre: string;
  asistencia: "sí" | "no";
  preferencias: string;
  canciones: string;
  timestamp: string;
}

/**
 * Obtener todos los registros RSVP desde Firebase
 */
export const getAllRSVPFromFirebase = async (): Promise<RSVPEntry[]> => {
  try {
    const rsvpRef = ref(database, 'rsvps');
    
    // Obtener una snapshot de los datos
    const snapshot = await get(rsvpRef);
    
    if (snapshot.exists()) {
      // Convertir el objeto a un array de entradas
      const data = snapshot.val();
      
      // Convertir los datos al formato esperado por el dashboard
      const entries: RSVPEntry[] = [];
      
      // Iterar sobre cada entrada
      Object.entries(data).forEach(([key, value]: [string, any]) => {
        // Verificar si es el nuevo formato o el antiguo
        if (value.nombre !== undefined) {
          // Es el nuevo formato, convertir al formato que espera el dashboard
          const nuevoFormato = value as NuevoRSVPEntry;
          entries.push({
            id: key,
            name: nuevoFormato.nombre,
            attendance: nuevoFormato.asistencia === "sí" ? "Sí asistiré" : "No podré asistir",
            allergies: nuevoFormato.preferencias || "",
            musicRecommendations: nuevoFormato.canciones || "",
            timestamp: nuevoFormato.timestamp
          });
        } else if (value.name !== undefined) {
          // Es el formato antiguo, usarlo directamente
          entries.push(value as RSVPEntry);
        }
      });
      
      console.log(`Datos obtenidos de Firebase: ${entries.length} registros`);
      return entries;
    } else {
      console.log('No hay datos en Firebase');
      return [];
    }
  } catch (error) {
    console.error('Error al obtener datos de Firebase:', error);
    return [];
  }
};

/**
 * Obtener todos los registros RSVP en el nuevo formato
 */
export const obtenerRSVPsNuevoFormato = async (): Promise<NuevoRSVPEntry[]> => {
  try {
    const rsvpRef = ref(database, 'rsvps');
    
    // Obtener una snapshot de los datos
    const snapshot = await get(rsvpRef);
    
    if (snapshot.exists()) {
      // Convertir el objeto a un array de entradas
      const data = snapshot.val();
      const entries: NuevoRSVPEntry[] = [];
      
      // Iterar sobre cada entrada
      Object.entries(data).forEach(([key, value]: [string, any]) => {
        // Verificar si es el nuevo formato
        if (value.nombre !== undefined) {
          entries.push(value as NuevoRSVPEntry);
        } else if (value.name !== undefined) {
          // Convertir el formato antiguo al nuevo
          const antiguoFormato = value as RSVPEntry;
          entries.push({
            nombre: antiguoFormato.name,
            asistencia: antiguoFormato.attendance === "Sí asistiré" ? "sí" : "no",
            preferencias: antiguoFormato.allergies || "",
            canciones: antiguoFormato.musicRecommendations || "",
            timestamp: antiguoFormato.timestamp
          });
        }
      });
      
      return entries;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error al obtener datos en nuevo formato:', error);
    return [];
  }
};

/**
 * Escuchar cambios en los registros RSVP en tiempo real
 */
export const listenToRSVPChanges = (callback: (entries: RSVPEntry[]) => void): (() => void) => {
  const rsvpRef = ref(database, 'rsvps');
  
  // Configurar el listener para cambios en tiempo real
  const unsubscribe = onValue(rsvpRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val();
      const entries: RSVPEntry[] = Object.values(data);
      callback(entries);
    } else {
      callback([]);
    }
  });
  
  // Devolver una función para cancelar la suscripción
  return unsubscribe;
};

/**
 * Actualizar un registro RSVP existente en Firebase
 */
export const updateRSVPInFirebase = async (entry: RSVPEntry): Promise<boolean> => {
  try {
    const rsvpRef = ref(database, 'rsvps/' + entry.id);
    await update(rsvpRef, entry);
    return true;
  } catch (error) {
    console.error('Error al actualizar datos en Firebase:', error);
    return false;
  }
};

/**
 * Guardar un registro RSVP en Firebase con el nuevo esquema
 */
export const guardarRSVPEnFirebase = async (datos: {
  nombre: string;
  asistencia: "sí" | "no";
  preferencias: string;
  canciones: string;
}): Promise<boolean> => {
  try {
    // Validar que el nombre no esté vacío
    if (!datos.nombre || datos.nombre.trim() === '') {
      console.error('El nombre no puede estar vacío');
      return false;
    }
    
    // Crear una referencia para generar una clave única automáticamente
    const rsvpsRef = ref(database, 'rsvps');
    const newRsvpRef = push(rsvpsRef);
    
    // Obtener la clave generada
    const newKey = newRsvpRef.key;
    
    if (!newKey) {
      console.error('No se pudo generar una clave única');
      return false;
    }
    
    // Crear el objeto con los datos y timestamp
    const rsvpData = {
      nombre: datos.nombre.trim(),
      asistencia: datos.asistencia,
      preferencias: datos.preferencias || '',
      canciones: datos.canciones || '',
      timestamp: new Date().toISOString()
    };
    
    // Guardar los datos en Firebase
    await set(newRsvpRef, rsvpData);
    
    console.log('Datos guardados exitosamente en Firebase con clave:', newKey);
    return true;
  } catch (error) {
    console.error('Error al guardar datos en Firebase:', error);
    return false;
  }
};

/**
 * Sincronizar datos locales con Firebase
 * Útil para la migración inicial de datos
 */
export const syncLocalDataToFirebase = async (entries: RSVPEntry[]): Promise<boolean> => {
  try {
    // Sincronizar cada entrada individualmente en lugar de hacer una actualización masiva
    let successCount = 0;
    
    // Procesar cada entrada por separado
    for (const entry of entries) {
      try {
        // Crear una referencia específica para esta entrada
        const entryRef = ref(database, `rsvps/${entry.id}`);
        
        // Guardar la entrada
        await set(entryRef, entry);
        
        successCount++;
      } catch (entryError) {
        console.warn(`Error al sincronizar entrada ${entry.id}:`, entryError);
        // Continuar con la siguiente entrada aunque esta haya fallado
      }
    }
    
    console.log(`${successCount} de ${entries.length} registros sincronizados con Firebase`);
    return successCount > 0;
  } catch (error) {
    console.error('Error al sincronizar datos con Firebase:', error);
    return false;
  }
};
